import React, {useEffect, useState} from 'react';
import {Control, Controller, useController, UseFormSetValue} from 'react-hook-form';
import cn from 'classnames';
import {InputGroup, InputNumber as RInputNumber} from 'rsuite';
import {InputNumberProps} from 'rsuite/esm/InputNumber/InputNumber';

import {getInputState} from 'shared/ui/Input/inputHelper';
import {useIsMount} from 'shared/lib/hooks/useIsMount';

import {getInputClassName} from '../inputState';

import styles from './InputNumber.module.scss';

interface IInputNumberProps extends InputNumberProps<string | number>{
    control: Control<any>;
    name: string,
    setValue: UseFormSetValue<any>,
    validate?: boolean;
    defaultValue?: number;
}

const InputNumber: React.FC<IInputNumberProps> = ({
    control,
    name,
    className,
    setValue,
    validate = true,
    defaultValue = 0,
    ...rest
}) => {

    const { field, fieldState } = useController({
        name,
        control,
        defaultValue,
    });
    
    const isMount = useIsMount();
    const [stateValue, setStateValue] = useState<number>(Number(field.value ?? defaultValue));

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setStateValue(Number(field.value ?? defaultValue));
    }, [field.value, defaultValue]);

    const state = getInputState({
        fieldState,
        value: field.value,
        validate,
    });

    const step = rest.step ?? 1;
    const min = rest.min ?? null;
    const max = rest.max ?? null;

    const isMinusDisabled = min !== null && stateValue - step < min;
    const isPlusDisabled = max !== null && stateValue + step > max;

    const handleMinus = () => {
        if (isMinusDisabled) {
            return;
        }
        setStateValue((prev) => Number(prev) - step);
    };
    const handlePlus = () => {
        if (isPlusDisabled) {
            return;
        }
        setStateValue((prev) => Number(prev) + step);
    };

    useEffect(() => {
        if (!isMount) {
            setValue(name, stateValue, { shouldDirty: true });
        }
    }, [stateValue, setValue, isMount, name]);

    return (
        <InputGroup
            className={getInputClassName(styles, state)}
        >
            <InputGroup.Button
                onClick={handleMinus}
                disabled={isMinusDisabled}
            >-</InputGroup.Button>

            <Controller
                name={name}
                control={control}
                /* eslint-disable-next-line no-unused-vars */
                render={({field: { value, onChange, onBlur, ...field}}) => (
                    <RInputNumber
                        className={cn(
                            'custom-input-number',
                            className,
                        )}
                        value={stateValue}
                        onChange={(val)=>{
                            setStateValue(Number(val));
                        }}
                        {...field}
                        {...rest}
                    />
                )}
            />
            <InputGroup.Button
                onClick={handlePlus}
                disabled={isPlusDisabled}
            >+</InputGroup.Button>
        </InputGroup>
    );
};

export default InputNumber;